import React, { useEffect, useState } from "react";

const API = process.env.REACT_APP_API_BASE_URL ?? "";

function getToken() {
  return localStorage.getItem("adminToken");
}

function money(value, currency = "CAD") {
  if (value === null || value === undefined) return "—";
  return `${currency} ${Number(value).toFixed(2)}`;
}

function isSuperAdmin() {
  try {
    const u = JSON.parse(localStorage.getItem("adminUser") || "{}");
    return u.role === "super_admin";
  } catch (e) { return false; }
}

async function overrideApprove(userId, name) {
  const amt = window.prompt(`Approve ${name || "this user"} for what CAD limit? (max 250)`, "250");
  if (amt === null) return false;
  const limit = Math.min(parseFloat(amt) || 250, 250);
  if (!window.confirm(`Approve ${name || "user"} for CAD $${limit}? This overrides the affordability decision and grants SNPL credit.`)) return false;
  try {
    const res = await fetch(`${API}/api/v2/cockpit/snpl/${userId}/approve`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ limit })
    });
    const j = await res.json();
    if (j.ok) { window.alert(`Approved for CAD $${j.limit}.`); return true; }
    window.alert(j.error || "Override failed");
    return false;
  } catch (e) {
    window.alert("Network error: " + e.message);
    return false;
  }
}

function ReviewQueue() {
  const [queue, setQueue] = React.useState([]);
  const [amounts, setAmounts] = React.useState({});
  const [busy, setBusy] = React.useState("");
  const [msg, setMsg] = React.useState("");

  async function loadQueue() {
    try {
      const res = await fetch(`${API}/api/v2/cockpit/snpl/review-queue`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const j = await res.json();
      if (j.ok) setQueue(j.users || []);
    } catch (e) { /* silent */ }
  }

  React.useEffect(() => { loadQueue(); }, []);

  async function act(userId, kind) {
    setBusy(userId + kind);
    setMsg("");
    try {
      const body = kind === "approve" ? { limit: parseFloat(amounts[userId]) || 250 } : { reason: "Manual review decision" };
      const res = await fetch(`${API}/api/v2/cockpit/snpl/${userId}/${kind}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(body)
      });
      const j = await res.json();
      if (j.ok) {
        setMsg(`User ${kind === "approve" ? "approved (CAD $" + j.limit + ")" : "declined"}.`);
        loadQueue();
      } else {
        setMsg(j.error || "Action failed");
      }
    } catch (e) {
      setMsg("Network error: " + e.message);
    }
    setBusy("");
  }

  if (queue.length === 0) return null;

  return (
    <div className="mb-6 border border-amber-300 rounded bg-amber-50 p-4">
      <h3 className="font-bold text-lg mb-1 text-amber-800">SNPL Review Queue ({queue.length})</h3>
      <p className="text-xs text-gray-600 mb-3">Users awaiting a manual affordability decision. Approve grants a discretionary limit; decline starts a 30-day cooldown.</p>
      {msg && <p className="text-sm text-blue-700 mb-2">{msg}</p>}
      <div className="space-y-2">
        {queue.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center gap-2 bg-white border rounded p-2">
            <div className="flex-1 min-w-[180px]">
              <div className="font-semibold text-sm">{u.firstName} {u.lastName}</div>
              <div className="text-xs text-gray-500">{u.email} · {u.country || "—"}</div>
            </div>
            <input
              type="number"
              min="1"
              max="250"
              placeholder="250"
              value={amounts[u.id] || ""}
              onChange={(e) => setAmounts({ ...amounts, [u.id]: e.target.value })}
              className="w-20 border rounded px-2 py-1 text-sm"
            />
            <button
              disabled={busy === u.id + "approve"}
              onClick={() => act(u.id, "approve")}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold px-3 py-1 rounded"
            >
              Approve
            </button>
            <button
              disabled={busy === u.id + "decline"}
              onClick={() => act(u.id, "decline")}
              className="bg-gray-600 hover:bg-gray-700 disabled:opacity-50 text-white text-sm font-semibold px-3 py-1 rounded"
            >
              Decline
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CockpitSNPLDecisions() {
  const [decisions, setDecisions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  const [filters, setFilters] = useState({
    approved: "",
    riskTier: "",
    source: "",
    stage: "",
    search: ""
  });

  async function loadData() {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const headers = { Authorization: `Bearer ${getToken()}` };

      const [listRes, statsRes] = await Promise.all([
        fetch(`${API}/api/v2/cockpit/snpl-decisions?${params.toString()}`, { headers }),
        fetch(`${API}/api/v2/cockpit/snpl-decisions/stats/summary`, { headers })
      ]);

      const listJson = await listRes.json();
      const statsJson = await statsRes.json();

      if (!listJson.ok) throw new Error(listJson.error || "Failed to load SNPL decisions");
      if (!statsJson.ok) throw new Error(statsJson.error || "Failed to load SNPL stats");

      setDecisions(listJson.decisions || []);
      setStats(statsJson.stats || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  async function loadDecision(id) {
    try {
      setError("");
      const res = await fetch(`${API}/api/v2/cockpit/snpl-decisions/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "Failed to load decision detail");
      setSelected(json.decision);
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="p-6 space-y-6">
      <ReviewQueue />
      <div>
        <h1 className="text-2xl font-bold text-gray-900">SNPL Decisions</h1>
        <p className="text-sm text-gray-500">
          Affordability decisions, income confidence, limits, and audit trail.
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl shadow p-4">
            <div className="text-xs text-gray-500">Total Decisions</div>
            <div className="text-2xl font-bold">{stats.total_decisions || 0}</div>
          </div>
          <div className="bg-white rounded-xl shadow p-4">
            <div className="text-xs text-gray-500">Approved</div>
            <div className="text-2xl font-bold">{stats.approved_count || 0}</div>
          </div>
          <div className="bg-white rounded-xl shadow p-4">
            <div className="text-xs text-gray-500">Reviews</div>
            <div className="text-2xl font-bold">{stats.review_count || 0}</div>
          </div>
          <div className="bg-white rounded-xl shadow p-4">
            <div className="text-xs text-gray-500">Low Confidence</div>
            <div className="text-2xl font-bold">{stats.low_confidence_count || 0}</div>
          </div>
          <div className="bg-white rounded-xl shadow p-4">
            <div className="text-xs text-gray-500">Avg Limit</div>
            <div className="text-2xl font-bold">
              {money(stats.avg_starting_limit || 0)}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <input
            className="border rounded-lg px-3 py-2 text-sm"
            placeholder="Search decision ref"
            value={filters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
          />

          <select className="border rounded-lg px-3 py-2 text-sm" value={filters.approved} onChange={(e) => updateFilter("approved", e.target.value)}>
            <option value="">All Decisions</option>
            <option value="true">Approved</option>
            <option value="false">Not Approved</option>
          </select>

          <select className="border rounded-lg px-3 py-2 text-sm" value={filters.riskTier} onChange={(e) => updateFilter("riskTier", e.target.value)}>
            <option value="">All Tiers</option>
            <option value="REVIEW">REVIEW</option>
            <option value="TIER_1">TIER_1</option>
            <option value="TIER_2">TIER_2</option>
            <option value="TIER_3">TIER_3</option>
            <option value="TIER_4">TIER_4</option>
          </select>

          <select className="border rounded-lg px-3 py-2 text-sm" value={filters.stage} onChange={(e) => updateFilter("stage", e.target.value)}>
            <option value="">All Stages</option>
            <option value="APPROVED">APPROVED</option>
            <option value="PRE_ZUM_GATE">PRE_ZUM_GATE</option>
            <option value="AFFORDABILITY_ASSESSMENT">AFFORDABILITY_ASSESSMENT (In Review)</option>
            <option value="LIMIT_CHECK">LIMIT_CHECK</option>
            <option value="WAITING_FOR_ZUM_AGGREGATION">WAITING_FOR_ZUM_AGGREGATION</option>
            <option value="MANUAL_ADMIN_APPROVAL">MANUAL_ADMIN_APPROVAL</option>
            <option value="MANUAL_ADMIN_DECLINE">MANUAL_ADMIN_DECLINE</option>
            <option value="BANK_ACCOUNT_CHECK">BANK_ACCOUNT_CHECK</option>
          </select>

          <select className="border rounded-lg px-3 py-2 text-sm" value={filters.source} onChange={(e) => updateFilter("source", e.target.value)}>
            <option value="">All Sources</option>
            <option value="MOCK">MOCK</option>
            <option value="SANDBOX">SANDBOX</option>
            <option value="LIVE">LIVE</option>
          </select>

          <button onClick={loadData} className="bg-blue-600 text-white rounded-lg px-4 py-2 text-sm font-semibold">
            Apply
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 rounded-lg p-3">{error}</div>}

      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left p-3">Decision</th>
              <th className="text-left p-3">Customer</th>
              <th className="text-left p-3">Quote</th>
              <th className="text-left p-3">Approved</th>
              <th className="text-left p-3">Risk Tier</th>
              <th className="text-left p-3">Income Confidence</th>
              <th className="text-left p-3">Stage</th>
              <th className="text-left p-3">Source</th>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="p-4" colSpan="10">Loading...</td></tr>
            ) : decisions.length === 0 ? (
              <tr><td className="p-4" colSpan="10">No SNPL decisions found.</td></tr>
            ) : (
              decisions.map((d) => (
                <tr key={d.id} className="border-t hover:bg-gray-50 cursor-pointer" onClick={() => loadDecision(d.id)}>
                  <td className="p-3 font-mono text-xs">{d.decisionReference}</td>
                  <td className="p-3">
                    {d.user ? `${d.user.firstName || ""} ${d.user.lastName || ""}`.trim() || d.user.email : "—"}
                    <div className="text-xs text-gray-500">{d.user?.email}</div>
                  </td>
                  <td className="p-3">{money(d.quoteAmount, d.currency || "CAD")}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded text-xs ${d.approved ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                      {d.approved ? "YES" : "NO"}
                    </span>
                  </td>
                  <td className="p-3">{d.riskTier || "—"}</td>
                  <td className="p-3">
                    {d.incomeConfidence || "—"}
                    <div className="text-xs text-gray-500">{money(d.monthlyIncome, d.currency || "CAD")}</div>
                  </td>
                  <td className="p-3">{d.stage || "—"}</td>
                  <td className="p-3">{d.source || "—"}</td>
                  <td className="p-3">{d.createdAt ? new Date(d.createdAt).toLocaleString() : "—"}</td>
                  <td className="p-3">
                    {!d.approved && d.user?.id && isSuperAdmin() && (
                      <button
                        onClick={async (e) => { e.stopPropagation(); const ok = await overrideApprove(d.user.id, `${d.user.firstName || ""} ${d.user.lastName || ""}`.trim()); if (ok) loadData(); }}
                        className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-2 py-1 rounded"
                      >
                        Approve User
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-end z-50" onClick={() => setSelected(null)}>
          <div className="bg-white w-full max-w-xl h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-xl font-bold">Decision Detail</h2>
                <p className="font-mono text-xs text-gray-500">{selected.decisionReference}</p>
              </div>
              <button className="text-gray-500" onClick={() => setSelected(null)}>✕</button>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
              <Detail label="Approved" value={selected.approved ? "YES" : "NO"} />
              <Detail label="Stage" value={selected.stage} />
              <Detail label="Quote Amount" value={money(selected.quoteAmount, selected.currency)} />
              <Detail label="Approved Amount" value={money(selected.approvedAmount, selected.currency)} />
              <Detail label="Risk Tier" value={selected.riskTier} />
              <Detail label="Starting Limit" value={money(selected.startingLimit, selected.currency)} />
              <Detail label="Score" value={selected.affordabilityScore} />
              <Detail label="Income Confidence" value={selected.incomeConfidence} />
              <Detail label="Monthly Income" value={money(selected.monthlyIncome, selected.currency)} />
              <Detail label="Observation Days" value={selected.incomeObservationDays} />
              <Detail label="Deposits 90d" value={selected.incomeDepositsDetected90d} />
              <Detail label="Current Balance" value={money(selected.currentBalance, selected.currency)} />
              <Detail label="Average Balance" value={money(selected.avgBalance, selected.currency)} />
              <Detail label="NSF 90d" value={selected.nsfCount90d} />
              <Detail label="Account Age Months" value={selected.accountAgeMonths} />
              <Detail label="Provider" value={selected.provider} />
              <Detail label="Aggregation Request" value={selected.aggregationRequestId || "—"} />
              <Detail label="Engine Version" value={selected.engineVersion} />
              <Detail label="Normalizer Version" value={selected.normalizerVersion} />
              <Detail label="Decision Version" value={selected.decisionServiceVersion} />
            </div>

            <div className="mt-6">
              <h3 className="font-semibold mb-2">Reasons</h3>
              <pre className="bg-gray-50 rounded-lg p-3 text-xs overflow-x-auto">
                {JSON.stringify(selected.reasons || [], null, 2)}
              </pre>
            </div>

            {!selected.approved && (selected.userId || (selected.user && selected.user.id)) && isSuperAdmin() && (
              <div className="mt-6 border-t pt-4">
                <button
                  onClick={async () => { const uid = selected.userId || (selected.user && selected.user.id); const ok = await overrideApprove(uid, selected.user ? `${selected.user.firstName || ""} ${selected.user.lastName || ""}`.trim() : ""); if (ok) { setSelected(null); loadData(); } }}
                  className="bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-4 py-2 rounded w-full"
                >
                  Override — Approve This User
                </button>
                <p className="text-xs text-gray-400 mt-2">Super admin only. Grants SNPL credit, overriding the affordability decision. Recorded to the audit trail.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="font-medium break-words">{value ?? "—"}</div>
    </div>
  );
}
